import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, Loader2, RotateCcw, FileSignature, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { maskCPF, validaCPF } from "../lib/validators";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TIPO_LABEL = { "12_meses": "Contrato 12 meses", mensal: "Contrato Mensal", renovacao: "Renovação", venda: "Venda" };

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function AssinarPedido() {
  const { token } = useParams();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [assinando, setAssinando] = useState(false);
  const [assinado, setAssinado] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/publico/pedido/${token}`)
      .then((r) => setPedido(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Pedido não encontrado"))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas drawing
  const getPos = (e) => {
    const c = canvasRef.current; if (!c) return null;
    const r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = getPos(e);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pt = getPos(e);
    if (!pt || !lastPt.current) return;
    ctx.strokeStyle = "#1B3A6B";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
  };
  const stopDraw = () => { drawing.current = false; lastPt.current = null; };
  const limparCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const assinar = async () => {
    if (!nome.trim() || nome.trim().length < 3) {
      toast.error("Informe seu nome completo"); return;
    }
    if (!validaCPF(cpf)) {
      toast.error("CPF inválido — verifique o número digitado"); return;
    }
    const c = canvasRef.current;
    if (!c) return;
    // Verificar se tem traços (ImageData)
    const ctx = c.getContext("2d");
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let hasInk = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) { hasInk = true; break; }
    }
    if (!hasInk) {
      toast.error("Assine no quadro acima"); return;
    }
    const b64 = c.toDataURL("image/png");
    setAssinando(true);
    try {
      await axios.post(`${BACKEND}/api/publico/pedido/${token}/assinar`, {
        nome: nome.trim(),
        cpf: cpf,
        assinatura_base64: b64,
      });
      setAssinado(true);
      toast.success("Proposta assinada com sucesso!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao assinar");
    } finally { setAssinando(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 size={32} className="animate-spin" style={{ color: "#1B3A6B" }} /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-zinc-100">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle size={48} className="mx-auto mb-3 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Link não disponível</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (assinado || pedido?.status === "concluido" || pedido?.status === "assinado") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-emerald-50">
        <Card className="p-8 max-w-md text-center" data-testid="sucesso-assinatura">
          <CheckCircle2 size={64} className="mx-auto mb-3 text-green-600" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: "#1B3A6B" }}>Proposta assinada!</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Recebemos sua assinatura. Nossa equipe entrará em contato em breve para concluir o processo.
          </p>
          <p className="text-xs text-muted-foreground">Locar Ativos Brasil — Obrigado pela parceria!</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4" data-testid="assinar-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <Card className="p-6">
          <h1 className="text-2xl font-bold mb-1" style={{ color: "#1B3A6B" }}>
            <FileSignature size={28} className="inline mr-2" />
            Proposta Comercial
          </h1>
          <p className="text-sm text-muted-foreground">Locar Ativos Brasil · Olá, <strong>{pedido.cliente_nome}</strong>!</p>
        </Card>

        <Card className="p-6">
          <h2 className="font-bold mb-3" style={{ color: "#1B3A6B" }}>Detalhes da proposta</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-muted-foreground">Tipo:</span> <strong>{TIPO_LABEL[pedido.tipo_contrato]}</strong></div>
            <div><span className="text-muted-foreground">Vendedor:</span> {pedido.vendedor_nome}</div>
            <div><span className="text-muted-foreground">Valor total:</span> <strong>{fmtMoney(pedido.valor_total)}</strong></div>
            <div><span className="text-muted-foreground">1ª fatura:</span> <strong>{fmtMoney(pedido.valor_primeira_fatura)}</strong></div>
            <div><span className="text-muted-foreground">Prazo:</span> {pedido.prazo_dias} dias</div>
          </div>
          {(pedido.itens || []).length > 0 && (
            <div className="mt-4 border-t pt-3">
              <strong className="text-sm">Itens incluídos:</strong>
              <ul className="text-sm mt-1 space-y-1">
                {pedido.itens.map((i, idx) => (
                  <li key={idx}>• {i.quantidade}× {i.nome} {i.valor_unitario ? `— ${fmtMoney(i.valor_unitario)}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {pedido.observacoes && (
            <div className="mt-4 p-3 rounded bg-blue-50 text-sm italic">{pedido.observacoes}</div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="font-bold mb-3" style={{ color: "#1B3A6B" }}>Confirme seus dados e assine</h2>
          <div className="space-y-3">
            <div>
              <Label>Nome completo *</Label>
              <Input data-testid="assinar-nome" value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div>
              <Label>CPF *</Label>
              <Input
                data-testid="assinar-cpf"
                value={cpf}
                onChange={(e) => setCpf(maskCPF(e.target.value))}
                placeholder="000.000.000-00"
                className={cpf && !validaCPF(cpf) ? "border-red-500" : ""}
              />
              {cpf && !validaCPF(cpf) && <p className="text-xs text-red-600 mt-1">CPF inválido</p>}
            </div>
            <div>
              <Label>Assinatura *</Label>
              <div className="border-2 border-dashed rounded-lg bg-white" style={{ borderColor: "#1B3A6B" }}>
                <canvas
                  ref={canvasRef}
                  width={700}
                  height={200}
                  className="w-full touch-none"
                  onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                  onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                  data-testid="assinatura-canvas"
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-muted-foreground">Use o mouse ou o dedo para assinar</p>
                <Button variant="ghost" size="sm" onClick={limparCanvas} data-testid="assinar-limpar">
                  <RotateCcw size={12} className="mr-1" /> Limpar
                </Button>
              </div>
            </div>
            <Button
              onClick={assinar}
              disabled={assinando}
              className="w-full text-white text-base py-6"
              style={{ background: "#F97316" }}
              data-testid="assinar-confirmar"
            >
              {assinando ? <><Loader2 size={16} className="animate-spin mr-2" /> Enviando…</> : <><CheckCircle2 size={16} className="mr-2" /> Assinar Proposta</>}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Ao clicar em "Assinar Proposta", você concorda com os termos descritos acima.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
